import { mountReportingSurface } from '../core/reportingSurface.js';
import { skeleton, beginRefresh } from '../core/loadingState.js';
import AuthController from '../services/AuthController.js';
import { guardedFetch } from '../core/navGeneration.js';
import { inPortfolio, pickerPageHTML, initPicker } from '../services/portfolio-view.js';
import { leadsHandoffScope, setLeadsHandoff } from '../core/routeHandoff.js';
import { setPerformancePeriod } from '../core/performanceNavigation.js';
import { defaultView, stripFigures } from './ad-performance-view.js';
import '../styles/performance.css';

const esc = value => String(value ?? '').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const number = value => value != null && Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '—';
// Explicit SVG dimensions keep these icons centered independently of icon-font loading.
const reportIcon = id => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" focusable="false" aria-hidden="true">${{
  magnets: '<path d="M4 19V5m0 14h16M8 15l4-5 4 2 4-7"/><path d="M16 5h4v4"/>',
  ads: '<path d="m15 4-8 4H3v8h4l8 4V4ZM15 9h2a3 3 0 0 1 0 6h-2M7 16l2 5h3l-2-4"/>',
  traffic: '<circle cx="12" cy="12" r="9"/><ellipse cx="12" cy="12" rx="4" ry="9"/><path d="M3 12h18"/>',
}[id]}</svg>`;
const scope = () => JSON.stringify(leadsHandoffScope());
let period = 30, periodScope, epoch = 0;
const intakeTitle = () => allowed('view_ad_metrics') ? 'Inquiries & calls' : 'Lead records';
const allowed = capability => (AuthController.getCapabilities() || []).includes(capability);
const reports = () => [
  ...(allowed('view_stats') ? [{id:'magnets',title:'Magnet performance',href:'/analytics',endpoint:'dashboard/stats'},
    {id:'traffic',title:'Website traffic',href:'/analytics/traffic',endpoint:'coreAnalyticsMetrics/overlay'}] : []),
  ...(allowed('view_ad_metrics') ? [{id:'ads',title:'Advertising',href:'/analytics/ad-performance',endpoint:'coreAdMetrics/overlay'}] : []),
].sort((a,b)=>['magnets','ads','traffic'].indexOf(a.id)-['magnets','ads','traffic'].indexOf(b.id));

const pendingMetric = label => `<span data-loading-state>${skeleton('value')}<small>${label}</small></span>`;
const pendingReport = id => ({
  magnets: pendingMetric('Opens') + pendingMetric('Completion rate'),
  ads: pendingMetric('Ad spend') + pendingMetric('Ad clicks'),
  traffic: pendingMetric('Sessions') + pendingMetric('Visitors'),
}[id]);

export function render() {
  if (inPortfolio()) return pickerPageHTML({section:'performance',title:'Performance',description:'Choose a client to see their results.'});
  if (periodScope !== scope()) { period = 30; periodScope = scope(); }
  return `<div class="page-shell performance-workspace" data-section="performance">
    <header class="performance-header"><div><h1>Performance</h1><p id="performance-range"></p></div>
      <label><span class="performance-sr">Reporting period</span><select id="performance-period">${[7,30,90].map(d=>`<option value="${d}"${d===period?' selected':''}>Last ${d} days</option>`).join('')}</select></label></header>
    ${allowed('view_magnets') ? `<section class="performance-intake" aria-labelledby="performance-intake-title" id="performance-intake"><div class="performance-intake-total" data-loading-state><h2 id="performance-intake-title">${intakeTitle()}</h2>${skeleton('value')}<span class="app-loading-sr" role="status">Loading performance</span></div><div class="performance-sources" aria-hidden="true">${skeleton('text')}${skeleton('short')}</div></section>` : ''}
    <section class="performance-reports" aria-label="Performance reports">${reports().map(r=>`<a class="performance-report" href="#${r.href}" data-report="${r.href}"><span class="performance-icon" aria-hidden="true">${reportIcon(r.id)}</span><div class="performance-report-name"><h2>${r.title}</h2></div><div class="performance-report-metrics" id="performance-${r.id}" aria-live="polite">${pendingReport(r.id)}</div><span aria-hidden="true">→</span></a>`).join('')}</section>
    <div id="performance-recovery"></div>
    <footer class="performance-footer"><a href="#/settings/integrations">Connections →</a>${allowed('view_magnets') ? '<a href="#/magnets">Manage magnets →</a>' : ''}</footer>
  </div>`;
}

export async function init() {
  if (inPortfolio()) return initPicker(AuthController.getConfig().BASE_URL, [{h:'Active magnets',td:r=>number(r.magnets?.active)}]);
  const host = document.querySelector('.performance-workspace');
  if (!host) return;
  mountReportingSurface('summary', host);
  host.querySelector('#performance-period').addEventListener('change',event=>{ period=Number(event.target.value); load(host); });
  host.addEventListener('click',event=>{
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    const report = event.target.closest('[data-report]');
    if (report) setPerformancePeriod(report.dataset.report,period);
  });
  await load(host);
}

async function read(endpoint, range) {
  const response = await guardedFetch(`${AuthController.getConfig().BASE_URL}/m5t/v5/${endpoint}${endpoint.includes('?')?'&':'?'}${range}`);
  if (!response.ok) throw new Error('Report unavailable');
  const data = await response.json();
  if (!data?.ok) throw new Error('Report unavailable');
  return data;
}
const metric = (value,label) => `<span><strong>${esc(value)}</strong><small>${esc(label)}</small></span>`;

async function load(host) {
  const ticket=++epoch, currentScope=scope(), days=period;
  const to=new Date().toISOString().slice(0,10), from=new Date(Date.now()-(days-1)*86400000).toISOString().slice(0,10);
  const range=new URLSearchParams({from,to});
  const live=()=>host.isConnected && ticket===epoch && currentScope===scope();

  host.querySelector('#performance-recovery').innerHTML='';
  const intake=host.querySelector('#performance-intake');
  const activityIntake=allowed('view_ad_metrics');
  const entries=reports();
  const finishRefresh = beginRefresh(host.querySelector('.performance-reports'), 'Updating performance');
  const results=await Promise.allSettled([
    ...(intake && !activityIntake ? [read('m5mLead?workspace=1&limit=1',range)] : []),
    ...entries.map(report=>read(report.endpoint,range)),
    ...(activityIntake ? [read('coreAdAccount',range)] : []),
  ]);
  finishRefresh();
  if(!live())return;
  host.querySelector('#performance-range').textContent=`${from} – ${to} · UTC`;
  let failures=0;
  if(intake) {
    const result=activityIntake ? results[entries.findIndex(r=>r.id==='ads')] : results.shift();
    const available=result.status==='fulfilled' && (activityIntake ? result.value.totals?.leads != null
      : result.value.workspace?.version===1 && Number.isFinite(result.value.total));
    if(!available) {
      failures++;
      intake.innerHTML=`<h2 id="performance-intake-title">${intakeTitle()}</h2><p>${activityIntake?'Activity totals':'Records'} are unavailable right now.</p>`;
    } else {
      const data=result.value;
      const figures=activityIntake ? stripFigures(data.totals,defaultView().sources) : null;
      const sources=activityIntake ? figures.split
        .filter(s=>s.key==='magnet'||s.count>0||(s.key==='lsa'&&data.totals.lsa_status))
        .map(s=>({source:{magnet:'Magnet',ad_call:'Google Ads call',lsa:'Google LSA'}[s.key],
          count:s.unavailable&&s.count===0?'—':`${number(s.count)}${s.unavailable||s.partial?'+':''}`}))
        : (Array.isArray(data.workspace.source_counts)?data.workspace.source_counts:[]).map(s=>({...s,count:number(s.count)}));
      const total=activityIntake ? `${number(figures.leads)}${figures.incomplete?'+':''}` : number(data.total);
      intake.innerHTML=`<div class="performance-intake-total"><h2 id="performance-intake-title">${intakeTitle()}</h2><strong>${total}</strong><a href="#/leads" data-open-leads>${activityIntake?'View activity':'View leads'} →</a>${figures?.incomplete?'<p class="performance-intake-note">Local Services totals incomplete</p>':''}</div>
        <div class="performance-sources">${sources.length ? sources.map(s=>`<a href="#/leads" data-open-leads data-source="${esc(s.source)}"><span>${esc(s.source==='Magnet'?'Website':s.source==='Google Ads call'?'Google Ads calls':s.source==='Google LSA'?'Local Services':s.source||'Other')}</span><strong>${esc(s.count)}</strong></a>`).join('') : '<p>No lead records in this period.</p>'}</div>`;
      for(const link of intake.querySelectorAll('[data-open-leads]')) link.addEventListener('click',event=>{
        if(event.metaKey||event.ctrlKey||event.altKey||event.shiftKey||!live())return;
        setLeadsHandoff({...leadsHandoffScope(),from,to,...(link.dataset.source?{source:link.dataset.source}:{}),returnTo:{hash:'#/performance',label:'Back to Performance'}});
      });
    }
  }
  entries.forEach((report,index)=>{
    const result=results[index], target=host.querySelector(`#performance-${report.id}`);
    if(result.status==='rejected') {failures++;target.textContent='Report unavailable';return;}
    const d=result.value;
    if(report.id==='magnets') {
      target.innerHTML=d.magnets?.total===0 ? '<span>No magnets yet</span>' : metric(number(d.funnel?.opens),'Opens')+metric(d.funnel?.completion_rate==null?'—':`${d.funnel.completion_rate}%`,'Completion rate');
    } else if(report.id==='ads') {
      const accountResult=results[entries.length];
      const accounts=accountResult?.status==='fulfilled' && Array.isArray(accountResult.value.data)
        ? accountResult.value.data.filter(account=>account.status!=='DELETED') : null;
      if(accounts?.length===0 && !d.connected) {
        target.textContent='No ad account connected';
      } else {
        // Website captures do not establish paid attribution. This card shows
        // provider metrics only; the intake above keeps all incoming activity.
        const spend=d.totals?.spend;
        target.innerHTML=metric(spend==null?'—':`$${Number(spend).toLocaleString(undefined,{maximumFractionDigits:2})}`,'Ad spend')
          +metric(number(d.totals?.clicks),'Ad clicks')
          +(accounts===null?'<small class="performance-data-note">Account status unavailable</small>':'');
        if(accounts===null)failures++;
      }
    } else if(!d.connected) {
      target.textContent='No activity reported for this period';
    } else {
      target.innerHTML=metric(number(d.totals?.sessions),'Sessions')+metric(number(d.totals?.users),'Visitors');
    }
  });
  if(failures) {
    const recovery=host.querySelector('#performance-recovery');
    recovery.innerHTML='<button type="button">Retry unavailable data</button>';
    recovery.querySelector('button').addEventListener('click',()=>load(host));
  }
}
